import React from "react";
import { DateTime } from "luxon";
import OverviewView from "./OverviewView";
import { useBookingDraft } from "../BookingDraftContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { useServiceCatalog } from "@/hooks/services/useServiceCatalog";
import { formatDurationSeconds } from "@/lib/utils";
import { useCreatePublicRecord } from "../hooks/useCreatePublicRecord";
import { getStoredClientAuth, isClientLoggedIn } from "../../client/ClientAuthContext";
import { useTranslation } from "react-i18next";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";

/**
 * Container for OverviewView
 * - reads BookingDraft
 * - resolves specialist name via StaffRegistry
 * - resolves services (name, price, duration) via service catalog snapshot
 * - formats date/time in salon tz
 * - handles contact form validation and submission
 */
export default function OverviewViewContainer({
  onBack,
  onEditSpecialist,
  onEditServices,
  onEditDateTime,
  onRestart,
  onOpenClientDashboard,
}: {
  onBack: () => void;
  onEditSpecialist: () => void;
  onEditServices: () => void;
  onEditDateTime: () => void;
  onRestart: () => void;
  onOpenClientDashboard?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const { draft, actions } = useBookingDraft();
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  const companySettings = getCompanyRegistry().getSettings();
  const currency = companySettings?.currency_short_title ?? "BYN";
  const phonePlaceholder = React.useMemo(() => {
    const rawPhoneCode = companySettings?.phone_code?.trim();
    const phoneCode = rawPhoneCode ? rawPhoneCode.replace(/^\+/, "") : "375";
    return `+${phoneCode} (__) ___-__-__`;
  }, [companySettings?.phone_code]);
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const luxonLocale = getLuxonLocale(language);

  // Use the same service snapshot the Services container uses
  const { data: snap } = useServiceCatalog({ enabled: true });

  // Specialist name + avatar
  let specialistName = t("widget.specialists.any_specialist", "Any specialist");
  let specialistAvatar: string | undefined = undefined;
  if (draft.specialistId && draft.specialistId !== "any") {
    const staffById = getStaffRegistry().getStaffByIdMap();
    const idNum = Number(draft.specialistId);
    const staff = staffById[idNum];
    if (staff) {
      specialistName = staff.name;
      specialistAvatar = staff.avatar || undefined;
    } else {
      specialistName = t("widget.common.specialist", "Specialist");
    }
  }

  const services = React.useMemo(() => {
    const ids = draft.serviceIds ?? [];
    if (!ids.length || !snap) {
      return ids.map((id) => ({ id, name: t("widget.common.service", "Service"), price: "—", duration: "—" }));
    }

    // Build a lookup from snap.services
    const svcById = new Map<string, any>();
    Object.values(snap.services || {}).forEach((s: any) => svcById.set(String(s.id), s));

    return ids.map((id) => {
      const s = svcById.get(id);
      if (!s) {
        return { id, name: t("widget.common.service", "Service"), price: "—", duration: "—" };
      }
      const priceStr = new Intl.NumberFormat(language).format(s.price_min ?? 0) + " " + currency;
      // Prefer master-specific duration if present (>0); otherwise fall back to service default
      const masterId = draft.specialistId && draft.specialistId !== "any" ? Number(draft.specialistId) : null;
      const custom = masterId ? Number(s?.staffDurationSeconds?.[masterId]) : NaN;
      const durSec = Number.isFinite(custom) && custom > 0 ? custom : Number(s?.duration_seconds ?? 0);
      const duration = formatDurationSeconds(durSec);
      return { id, name: s.title, price: priceStr, duration };
    });
  }, [draft.serviceIds, snap, currency, language, t]);

  const dt = draft.startISO ? DateTime.fromISO(draft.startISO).setZone(tz) : null;
  const dateLabel = dt ? dt.setLocale(luxonLocale).toFormat("d LLLL yyyy") : "—";
  const timeLabel = dt ? dt.toFormat("HH:mm") : "—";

  // mutation
  const createRecordMutation = useCreatePublicRecord();
  const createRecord = createRecordMutation.mutate;
  const isLoading = createRecordMutation.status === "pending";
  const [errors, setErrors] = React.useState<{ name?: string; phone?: string; email?: string }>({});

  // Pre-populate contact fields from stored client auth (if logged in)
  React.useEffect(() => {
    const auth = getStoredClientAuth();
    if (auth) {
      // Only set if draft fields are empty (don't overwrite user input)
      if (!draft.contact?.phone && auth.phone) {
        actions.setClientPhone(auth.phone);
      }
      if (!draft.contact?.name && auth.name) {
        actions.setClientName(auth.name);
      }
    }
  }, []); // Run once on mount

  // Local status for the UI panel
  const [status, setStatus] = React.useState<
    | undefined
    | { type: "success" | "error"; message: string; actionLabel?: string; onAction?: () => void }
  >(undefined);

  const validate = () => {
    const e: typeof errors = {};
    if (!draft.contact?.name?.trim()) e.name = t("widget.common.validation.required", "Please fill in this field");
    const phone = draft.contact?.phone?.trim() ?? "";
    if (!phone) {
      e.phone = t("widget.common.validation.required", "Please fill in this field");
    } else if (!phone.startsWith("+")) {
      e.phone = t("widget.common.validation.phone_country_code", "Phone number must start with the country code (for example, +375)");
    }
    const email = draft.contact?.email?.trim() ?? "";
    if (email && !EMAIL_REGEX.test(email)) {
      e.email = t("widget.common.validation.email_invalid", "Invalid email");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Translation/UX registry for backend errors
  const ERROR_MAP: Record<
    string,
    { message: string; button?: { label: string; onClick: () => void }; adjustDraft?: () => void }
  > = {
    "Selected time is no longer available": {
      message: t("widget.overview.time_unavailable", "The selected time is no longer available"),
      adjustDraft: () => {
        // keep date, clear concrete time only
        actions.setStartISO(null);
      },
      button: {
        label: t("widget.overview.choose_another_time", "Choose another time"),
        onClick: () => {
          onEditDateTime();
        },
      },
    },
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Clear previous status (do not touch the draft yet)
    setStatus(undefined);

    createRecord(draft, {
      onSuccess: () => {
        // 200 OK:
        //  - reset the draft
        //  - show success panel with action to go to the very first view
        actions.reset();
        setStatus({
          type: "success",
          message: t("widget.overview.success", "Your booking has been created successfully"),
          actionLabel: t("widget.overview.create_new", "Create another booking"),
          onAction: onRestart,
        });
      },
      onError: (err: unknown) => {
        // Expect FastAPI shape: { status: 'error', error: '...' }, status_code = 400
        const ax = err as import("axios").AxiosError<any>;
        // Try to extract custom message; otherwise fall back to Error.message
        const raw =
          (ax?.response?.data && (ax.response.data.error || ax.response.data.message)) ||
          (ax?.message ?? "Unknown error");
        const known = ERROR_MAP[String(raw)];

        if (known?.adjustDraft) known.adjustDraft();

        setStatus({
          type: "error",
          message: known ? known.message : String(raw),
          actionLabel: known?.button?.label,
          onAction: known?.button?.onClick,
        });

        // Keep draft as-is (except potential time clear above) — do not reset
        // eslint-disable-next-line no-console
        console.error("[Overview] create booking failed:", raw, err);
      },
    });
  };

  // Only offer signup when client accounts are enabled and the user is not logged in.
  const isLoggedIn = isClientLoggedIn();
  const canCreateClientAccount = companySettings?.client_account_enabled === true;
  const handleOpenDashboard = !isLoggedIn && canCreateClientAccount ? onOpenClientDashboard : undefined;

  return (
    <OverviewView
      specialistName={specialistName}
      specialistAvatar={specialistAvatar}
      services={services}
      dateLabel={dateLabel}
      timeLabel={timeLabel}
      phonePlaceholder={phonePlaceholder}
      onBack={onBack}
      onEditSpecialist={onEditSpecialist}
      onEditServices={onEditServices}
      onEditDateTime={onEditDateTime}

      // Controlled contact binding
      name={draft.contact?.name ?? ""}
      phone={draft.contact?.phone ?? ""}
      email={draft.contact?.email ?? ""}
      comment={draft.contact?.comment ?? ""}
      confirmMethod={draft.confirmMethod ?? 1}

      onChangeName={actions.setClientName}
      onChangePhone={actions.setClientPhone}
      onChangeEmail={actions.setClientEmail}
      onChangeComment={actions.setClientComment}
      onChangeConfirmMethod={actions.setConfirmMethod}

      errors={errors}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      status={status}
      onOpenClientDashboard={handleOpenDashboard}
    />
  );
}
